/**
 * Spec 040: a staff member's feed — what it did and what was done to it, wherever it runs, with
 * who did it: «Костя», «Саня», «агент Кости», «агент Сани». New lines come by themselves every
 * 5 seconds; «ещё» opens a line's details. First — what it does now and the subscription's limits
 * (Spec 045); then what it waits for from people, answered right here; under the lines —
 * «Написать сотруднику», which puts the words into its console signed with the writer's name; at
 * the bottom — the archive of the feed older than 7 days.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Feed, type FeedArchive, type FeedEvent, type StaffState } from "@/api/feed";
import { AgentRequests, Overview } from "@/api/resources";
import type { AgentRequestDecision, WaitingItem } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Caption, Chip, EmptyNote } from "@/kit";
import { decidedText, decisionLabels, ruleLabel, ruleOffer } from "@/lib/agents";
import { cn } from "@/lib/cn";
import { archiveLine, clock, isLoud, KIND_LABEL, mergeEvents, waitsLine } from "@/lib/feed";

const EVERY = 5_000;
const PAGE = 100;
const ARCHIVE_PAGE = 200;

/** The details of a line: whole when kept whole, else how much more is on the PC. */
function DetailText({ text, chars }: { text: string; chars: number | null }) {
  return (
    <div className="mt-1 space-y-1">
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-card border border-p-border bg-p-surface-2 px-3 py-2 font-mono text-meta text-p-text">
        {text}
      </pre>
      {chars != null && chars > text.length && (
        <p className="text-meta text-p-text-muted">
          Показано {text.length.toLocaleString("ru-RU")} из {chars.toLocaleString("ru-RU")} знаков: целиком — в консоли на ПК.
        </p>
      )}
    </div>
  );
}

function EventRow({ e, detail, onMore }: { e: FeedEvent; detail: string | undefined; onMore: () => void }) {
  return (
    <li className={cn("flex gap-3 border-b border-p-border px-4 py-2.5 last:border-0", e.helper && "border-l-2 border-l-p-border-strong pl-6")}>
      <span className="w-11 shrink-0 pt-0.5 font-mono text-meta text-p-text-muted">{clock(e.at)}</span>
      <div className="min-w-0 flex-1">
        <p className={cn("break-words text-body text-p-text", isLoud(e) && "font-semibold")}>
          {e.helper && <span className="font-normal text-p-text-muted">{e.helper} · </span>}
          {e.text}
        </p>
        <p className="text-meta text-p-text-muted">{[KIND_LABEL[e.kind] ?? e.kind, e.by].filter(Boolean).join(" · ")}</p>
        {e.has_detail && detail === undefined && (
          <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={onMore}>ещё</button>
        )}
        {detail !== undefined && <DetailText text={detail} chars={e.detail_chars} />}
      </div>
    </li>
  );
}

/** Spec 045: what it does now — its order and minutes, model and effort, the subscription's
 * 5-hour and weekly limits with their resets, and how fresh the snapshot is. */
export function Now({ state }: { state: StaffState | null }) {
  if (!state) return null;
  return (
    <section aria-label="Сейчас" className="space-y-1 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <Caption>{state.running ? "Сейчас" : "Последний запуск"}</Caption>
      <p className="break-words text-body text-p-text">{state.line}</p>
    </section>
  );
}

/** What the staff member waits for from people: a permission answered here, the rest in «Решить».
 * Spec 041: «Разрешить и не спрашивать» — the third button, when the server offers a rule. */
function Waiting({ items }: { items: WaitingItem[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const decide = useMutation({
    mutationFn: ({ id, decision, always }: { id: string; decision: "grant" | "refuse"; always?: boolean }) =>
      AgentRequests.decide(id, { decision, ...(always ? { always } : {}) } as AgentRequestDecision),
    onSuccess: (_, v) => {
      toast.success(decidedText("permission", v.decision, v.always));
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  if (!items.length) return null;
  const labels = decisionLabels("permission");
  return (
    <section className="space-y-2" aria-label="Ждёт людей">
      {items.map((w) => (
        <div key={w.request_id ?? w.since} className="space-y-2 rounded-card border border-p-blocked bg-p-blocked-tint px-4 py-3">
          <p className="text-body font-semibold text-p-blocked">
            {w.request_kind && w.request_kind !== "handover" ? waitsLine(w.request_kind, w.since) : `ждёт с ${clock(w.since)}`}
          </p>
          {w.request_kind === "permission" && w.permission ? (
            <>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-card border border-p-border bg-p-surface px-3 py-2 font-mono text-meta text-p-text">
                {w.permission.tool}: {w.permission.input}
              </pre>
              <div className="flex flex-wrap gap-2">
                <Button size="compact" variant="strong" loading={decide.isPending}
                  onClick={() => w.request_id && decide.mutate({ id: w.request_id, decision: "grant" })}>
                  {labels.grant}
                </Button>
                {ruleOffer(w.permission) && (
                  <Button size="compact" variant="secondary" disabled={decide.isPending}
                    onClick={() => w.request_id && decide.mutate({ id: w.request_id, decision: "grant", always: true })}>
                    {ruleLabel(ruleOffer(w.permission)!)}
                  </Button>
                )}
                <Button size="compact" variant="secondary" disabled={decide.isPending}
                  onClick={() => w.request_id && decide.mutate({ id: w.request_id, decision: "refuse" })}>
                  {labels.refuse}
                </Button>
              </div>
              <p className="text-meta text-p-text-muted">Тот же вопрос — в консоли на ПК: кто ответит первым, тот и решил.</p>
            </>
          ) : (
            <p className="text-body text-p-text">
              {w.text} <Link to="/" className="font-semibold text-p-link">Ответить в «Решить»</Link>
            </p>
          )}
        </div>
      ))}
    </section>
  );
}

/** «Написать сотруднику»: the words go into its console signed with my name, and into the feed. */
function Message({ agentId, running, me }: { agentId: string; running: boolean; me: string | null }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: () => Feed.write(agentId, text.trim()),
    onSuccess: (out) => {
      setText("");
      toast.success(`Ушло в консоль сотрудника с подписью «[${out.signature}]»`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!running) return null;
  return (
    <section className="space-y-2" aria-label="Написать сотруднику">
      <TextArea
        label="Написать сотруднику"
        hint={`От: ${me ?? "тебя"}. Уйдёт в его консоль с подписью и в ленту; занят ходом — прочтёт после него. Ждёт ответа на разрешение — сначала ответь на него.`}
        value={text}
        rows={2}
        maxLength={4000}
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        <Button variant="strong" disabled={!text.trim()} loading={send.isPending} onClick={() => send.mutate()}>Отправить</Button>
      </div>
    </section>
  );
}

function ArchiveSheet({ agentId, archive, onClose }: { agentId: string; archive: FeedArchive; onClose: () => void }) {
  const [offset, setOffset] = useState(0);
  const page = useQuery({
    queryKey: ["agents", "feed-archive", agentId, archive.id, offset],
    queryFn: () => Feed.archive(agentId, archive.id, offset, ARCHIVE_PAGE),
  });
  const total = page.data?.total ?? 0;
  return (
    <Sheet open onClose={onClose} title={archiveLine(archive)}>
      {page.isLoading ? (
        <SkeletonList count={3} />
      ) : page.isError || !page.data ? (
        <EmptyNote>{page.error instanceof Error ? page.error.message : "Архив не прочитался."}</EmptyNote>
      ) : (
        <div className="flex flex-col gap-2">
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {page.data.events.map((e) => (
              <li key={e.id} className="border-b border-p-border px-4 py-2.5 last:border-0">
                <p className="break-words text-body text-p-text">{e.helper ? `${e.helper} · ` : ""}{e.text}</p>
                <p className="text-meta text-p-text-muted">
                  {[new Date(e.at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }), KIND_LABEL[e.kind] ?? e.kind, e.by].filter(Boolean).join(" · ")}
                </p>
                {e.detail && (
                  <details className="mt-1">
                    <summary className="min-h-11 cursor-pointer text-meta font-semibold text-p-link">подробности</summary>
                    <DetailText text={e.detail} chars={e.detail_chars} />
                  </details>
                )}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="compact" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - ARCHIVE_PAGE))}>Раньше</Button>
            <Button size="compact" variant="secondary" disabled={offset + ARCHIVE_PAGE >= total} onClick={() => setOffset(offset + ARCHIVE_PAGE)}>Дальше</Button>
            <span className="text-meta text-p-text-muted">{total ? `${offset + 1}–${Math.min(offset + ARCHIVE_PAGE, total)} из ${total}` : "событий нет"}</span>
          </div>
          <p className="text-meta text-p-text-muted">Достаётся только для чтения: в ленту и в базу архив не возвращается.</p>
        </div>
      )}
    </Sheet>
  );
}

/** The files of the feed older than 7 days: «Достать», download, delete with a confirmation. */
function Archive({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["agents", "feed-archive", agentId], queryFn: () => Feed.archives(agentId) });
  const [reading, setReading] = useState<FeedArchive | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // The same function every time: the feed above redraws every 5 seconds, and a new one would
  // make the open sheet give its focus back and take it again.
  const close = useCallback(() => setReading(null), []);
  const remove = useMutation({
    mutationFn: (id: string) => Feed.remove(agentId, id),
    onSuccess: () => {
      setDeleting(null);
      toast.success("Файл архива удалён");
      void qc.invalidateQueries({ queryKey: ["agents", "feed-archive", agentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const download = useMutation({
    mutationFn: (a: FeedArchive) => Feed.download(agentId, a),
    onError: (e: Error) => toast.error(e.message),
  });
  if (!list.data?.length) return null;
  return (
    <section className="space-y-1.5">
      <Caption>Архив ленты</Caption>
      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {list.data.map((a) => (
          <li key={a.id} className="flex flex-col gap-1 border-b border-p-border px-4 py-3 last:border-0">
            <span className="text-body text-p-text">{archiveLine(a)}</span>
            <div className="flex flex-wrap items-center gap-x-4">
              <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setReading(a)}>Достать</button>
              <button type="button" className="min-h-11 text-meta font-semibold text-p-link" disabled={download.isPending} onClick={() => download.mutate(a)}>Скачать</button>
              {deleting === a.id ? (
                <>
                  <button type="button" className="min-h-11 text-meta font-semibold text-p-text" disabled={remove.isPending} onClick={() => remove.mutate(a.id)}>Точно удалить</button>
                  <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setDeleting(null)}>Отмена</button>
                  <span className="text-meta text-p-text-muted">Файл удалится насовсем; в ленте останется, кто и когда его удалил.</span>
                </>
              ) : (
                <button type="button" className="min-h-11 text-meta font-semibold text-p-text-muted" onClick={() => setDeleting(a.id)}>Удалить</button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {reading && <ArchiveSheet agentId={agentId} archive={reading} onClose={close} />}
    </section>
  );
}

export function AgentFeed({ agentId, running }: { agentId: string; running: boolean }) {
  const toast = useToast();
  const [important, setImportant] = useState(false);
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [moreBefore, setMoreBefore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [older, setOlder] = useState(false);
  const [details, setDetails] = useState<Record<number, string>>({});
  const [state, setState] = useState<StaffState | null>(null);
  const last = useRef<number | null>(null);
  const overview = useQuery({ queryKey: ["overview"], queryFn: Overview.get, refetchInterval: 30_000 });
  const waiting = (overview.data?.for_me ?? []).filter((w) => w.kind === "agent_request" && w.item.id === agentId);

  useEffect(() => {
    let stop = false;
    last.current = null;
    setEvents([]);
    setState(null);
    setLoaded(false);
    setFailed(null);
    const tick = async () => {
      try {
        const first = last.current === null;
        const page = first
          ? await Feed.read(agentId, { limit: PAGE, important })
          : await Feed.read(agentId, { after: last.current, limit: 200, important });
        if (stop) return;
        if (first) setMoreBefore(page.more_before);
        if (page.state !== undefined) setState(page.state);
        if (page.events.length) setEvents((known) => mergeEvents(known, page.events));
        last.current = page.last_id ?? last.current ?? 0;
        setLoaded(true);
        setFailed(null);
      } catch (e) {
        if (!stop) setFailed(e instanceof Error ? e.message : "Лента не загрузилась");
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), EVERY);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [agentId, important]);

  const earlier = async () => {
    if (!events.length) return;
    setOlder(true);
    try {
      const page = await Feed.read(agentId, { before: events[0].id, limit: PAGE, important });
      setEvents((known) => mergeEvents(known, page.events));
      setMoreBefore(page.more_before);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не загрузилось");
    } finally {
      setOlder(false);
    }
  };

  const more = async (id: number) => {
    try {
      const e = await Feed.detail(agentId, id);
      setDetails((known) => ({ ...known, [id]: e?.detail ?? "Подробностей нет." }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Не загрузилось");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Now state={state} />
      <Waiting items={waiting} />
      <div className="flex flex-wrap items-center gap-2">
        <Chip on={!important} onClick={() => setImportant(false)}>Всё</Chip>
        <Chip on={important} onClick={() => setImportant(true)}>Важное</Chip>
        <span className="text-meta text-p-text-muted">новое приходит само</span>
      </div>
      {failed && <p className="text-meta text-p-blocked" role="status">{failed}</p>}
      {!loaded && !failed && <SkeletonList count={3} />}
      {loaded && events.length === 0 && (
        <EmptyNote>
          {important
            ? "Важного пока нет: ни ожиданий, ни ответов, ни сбоев."
            : "В ленте пока пусто. Она пишется, когда сотрудник работает в «Агентах» на ПК обновлённой версии, и хранится 7 дней — старое в архиве ниже."}
        </EmptyNote>
      )}
      {moreBefore && events.length > 0 && (
        <div>
          <Button size="compact" variant="secondary" loading={older} onClick={() => void earlier()}>Раньше</Button>
        </div>
      )}
      {events.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface" aria-live="polite">
          {events.map((e) => <EventRow key={e.id} e={e} detail={details[e.id]} onMore={() => void more(e.id)} />)}
        </ul>
      )}
      <Message agentId={agentId} running={running} me={overview.data?.me.name ?? null} />
      <Archive agentId={agentId} />
    </div>
  );
}
